import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    Clock,
    ListTodo,
    CheckCircle2,
    AlertTriangle,
    CheckSquare,
    ClipboardList,
    Calendar,
    ArrowRight,
    Loader2,
    Inbox,
    ChevronRight,
} from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const STATUS_TABS = [
    { key: 'pending', label: 'Pending', icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50', activeBg: 'bg-amber-100', ring: 'ring-amber-300' },
    { key: 'in_progress', label: 'In Progress', icon: ListTodo, color: 'text-blue-600', bg: 'bg-blue-50', activeBg: 'bg-blue-100', ring: 'ring-blue-300' },
    { key: 'completed', label: 'Completed', icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50', activeBg: 'bg-green-100', ring: 'ring-green-300' },
    { key: 'overdue', label: 'Overdue', icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50', activeBg: 'bg-red-100', ring: 'ring-red-300' },
];

const TYPE_META = {
    task: { icon: CheckSquare, label: 'Task', badgeBg: 'bg-blue-100 text-blue-700' },
    survey: { icon: ClipboardList, label: 'Survey', badgeBg: 'bg-purple-100 text-purple-700' },
};

const formatDate = (d) => {
    if (!d) return null;
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const MyActivitiesPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const activeStatus = searchParams.get('status') || 'pending';

    const [activities, setActivities] = useState([]);
    const [counts, setCounts] = useState({ pending: 0, in_progress: 0, completed: 0, overdue: 0 });
    const [loading, setLoading] = useState(true);

    // Fetch counts
    useEffect(() => {
        const fetchCounts = async () => {
            try {
                const res = await api.get('/my-activities/counts');
                setCounts(res.data.counts || { pending: 0, in_progress: 0, completed: 0, overdue: 0 });
            } catch (err) {
                console.error('Error fetching counts:', err);
            }
        };
        fetchCounts();
    }, [activities]); // Refresh counts whenever activities change

    // Fetch activities for active tab
    useEffect(() => {
        const fetchActivities = async () => {
            setLoading(true);
            try {
                const res = await api.get(`/my-activities?status=${activeStatus}`);
                setActivities(res.data.activities || []);
            } catch (err) {
                console.error('Error fetching activities:', err);
                setActivities([]);
            } finally {
                setLoading(false);
            }
        };
        fetchActivities();
    }, [activeStatus]);

    const setActiveTab = (key) => {
        setSearchParams({ status: key });
    };

    const handleAction = async (activity) => {
        const basePath = user?.is_admin ? '/admin' : '/employee';
        if (activity.type === 'task') {
            navigate(`${basePath}/tasks/${activity.assignment_id || activity.id}`);
        } else if (activity.type === 'survey') {
            navigate(user?.is_admin ? '/admin/my-surveys' : `${basePath}/surveys`);
        }
    };

    const handleMarkComplete = async (activity) => {
        if (activity.type === 'task') {
            try {
                const assignmentId = activity.assignment_id || activity.id;
                await api.post(`/tasks/employee/${assignmentId}/complete`, {});
                // Refresh
                setActivities(prev => prev.filter(a => !(a.type === activity.type && a.id === activity.id)));
            } catch (err) {
                console.error('Error marking complete:', err);
            }
        }
    };

    const handleStartTask = async (activity) => {
        if (activity.type === 'task') {
            try {
                const assignmentId = activity.assignment_id || activity.id;
                await api.put(`/tasks/employee/${assignmentId}/status`, { status: 'in_progress' });
                // Navigate to task detail page
                const basePath = user?.is_admin ? '/admin' : '/employee';
                navigate(`${basePath}/tasks/${assignmentId}`);
            } catch (err) {
                console.error('Error starting task:', err);
            }
        }
    };

    const totalActive = counts.pending + counts.overdue;

    return (
        <div className="max-w-5xl mx-auto p-6">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900">My Activities</h1>
                <p className="text-gray-500 mt-1">
                    {totalActive > 0
                        ? `You have ${totalActive} item${totalActive !== 1 ? 's' : ''} requiring attention`
                        : 'You\'re all caught up!'
                    }
                </p>
            </div>

            {/* Status tabs */}
            <div className="flex gap-2 mb-6 flex-wrap">
                {STATUS_TABS.map(tab => {
                    const Icon = tab.icon;
                    const count = counts[tab.key] || 0;
                    const isActive = activeStatus === tab.key;

                    return (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={`
                                flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium
                                transition-all duration-200 border
                                ${isActive
                                    ? `${tab.activeBg} ${tab.color} border-current ring-2 ${tab.ring} shadow-sm`
                                    : `bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50`
                                }
                            `}
                        >
                            <Icon className="w-4 h-4" />
                            <span>{tab.label}</span>
                            {count > 0 && (
                                <span className={`
                                    ml-1 px-1.5 py-0.5 text-[11px] font-bold rounded-full
                                    ${isActive ? 'bg-white/70 text-current' : 'bg-gray-100 text-gray-600'}
                                `}>
                                    {count}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Activity list */}
            <div className="space-y-3">
                {loading ? (
                    <div className="flex items-center justify-center py-16">
                        <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                    </div>
                ) : activities.length === 0 ? (
                    <div className="text-center py-16">
                        <Inbox className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 font-medium">No {activeStatus.replace('_', ' ')} activities</p>
                        <p className="text-gray-400 text-sm mt-1">
                            {activeStatus === 'completed' ? 'Completed items will show up here' : 'Any assigned tasks or surveys will appear here'}
                        </p>
                    </div>
                ) : (
                    activities.map((activity, idx) => {
                        const meta = TYPE_META[activity.type] || TYPE_META.task;
                        const ItemIcon = meta.icon;
                        const isOverdue = activity.status !== 'completed' && activity.due_date && new Date(activity.due_date) < new Date();

                        return (
                            <div
                                key={`${activity.type}-${activity.id}-${idx}`}
                                className={`
                                    bg-white border rounded-xl p-4 hover:shadow-md transition-all duration-200
                                    ${isOverdue ? 'border-red-200 bg-red-50/30' : 'border-gray-200'}
                                `}
                            >
                                <div className="flex items-start gap-3">
                                    {/* Icon */}
                                    <div className={`
                                        w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0
                                        ${activity.type === 'task' ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'}
                                    `}>
                                        <ItemIcon className="w-5 h-5" />
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h3 className="font-semibold text-gray-900 truncate">{activity.title}</h3>
                                            <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase rounded-full ${meta.badgeBg}`}>
                                                {meta.label}
                                            </span>
                                            {activity.priority && activity.priority !== 'medium' && (
                                                <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase rounded-full ${activity.priority === 'high' || activity.priority === 'urgent'
                                                    ? 'bg-red-100 text-red-700'
                                                    : 'bg-gray-100 text-gray-600'
                                                    }`}>
                                                    {activity.priority}
                                                </span>
                                            )}
                                            {isOverdue && (
                                                <span className="px-2 py-0.5 text-[10px] font-semibold uppercase rounded-full bg-red-100 text-red-700">
                                                    Overdue
                                                </span>
                                            )}
                                        </div>

                                        {activity.description && (
                                            <p className="text-sm text-gray-500 mt-1 line-clamp-2">{activity.description}</p>
                                        )}

                                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                                            {activity.due_date && (
                                                <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-500 font-medium' : ''}`}>
                                                    <Calendar className="w-3.5 h-3.5" />
                                                    Due {formatDate(activity.due_date)}
                                                </span>
                                            )}
                                            {activity.completed_at && (
                                                <span className="flex items-center gap-1 text-green-500">
                                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                                    Completed {formatDate(activity.completed_at)}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        {['pending', 'viewed', 'not_started'].includes(activity.status) && activity.type === 'task' && (
                                            <button
                                                onClick={() => handleStartTask(activity)}
                                                className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                                            >
                                                Start
                                            </button>
                                        )}
                                        {(activity.status === 'in_progress' || ['pending', 'viewed', 'not_started'].includes(activity.status)) && activity.type === 'task' && (
                                            <button
                                                onClick={() => handleMarkComplete(activity)}
                                                className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
                                            >
                                                Complete
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleAction(activity)}
                                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                                            title="View details"
                                        >
                                            <ChevronRight className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default MyActivitiesPage;
